import { POSTAL_ADDRESS } from "@/lib/constants";

const CONTENT = {
  privacy: {
    label: "Legal",
    title: "Privacy Policy",
    updated: "August 2026",
    entityLine: "Biology Trader Education LLC",
    showPostal: true,
    body: [
      "This website collects information that you choose to submit.",
      "This may include:",
      {
        list: [
          "Your name and email address when you request the book",
          "Your email address for delivery emails and related The Biology Trader updates",
          "Your name, email address, and message when you contact us",
          "Your star rating, written review, email address, and optional name when you submit a reader review",
          "Technical information used for security, spam prevention, rate limiting, and basic website analytics",
        ],
      },
      "Review email addresses are never displayed publicly. Public reviews may display the submitted name or “Anonymous trader,” the rating, review text, and Verified download status.",
      "Review submissions are moderated before publication to identify spam, abusive content, and other inappropriate or irrelevant material.",
      "You may unsubscribe from marketing emails using the unsubscribe link included in those emails.",
      "Information is used to deliver requested resources, operate the mailing list, respond to messages, process and moderate reader reviews, maintain website security, and understand general website usage.",
      "Biology Trader Education LLC does not sell personal information.",
      "Information may be processed by service providers used to operate the website, database, email delivery, analytics, hosting, and security systems. These providers receive information only as necessary to provide their services.",
      "You may request access to or deletion of your submitted information by emailing: hello@thebiologytrader.com",
    ],
    sections: [
      {
        heading: "Individually issued book copies",
        paras: [
          "Although the book is free, each copy is individually issued to one reader. Send thebiologytrader.com/book to anyone you like. Posting the file itself publicly, on file-sharing platforms, in public groups, or for resale, is not permitted, and a copy distributed that way may be traced back to the download record it came from.",
        ],
      },
      {
        heading: "Book download links",
        paras: [
          "Each reader receives an individual link to their copy of the book. The link does not expire and may be used to download the file again at any time.",
          "A link showing signs of public distribution or unusually widespread use may be cancelled. A new individually issued copy may be obtained by submitting the request form again.",
        ],
      },
      {
        heading: "Reference code in the book file",
        paras: [
          "Each copy of the book carries a short reference code, for example TBT-1a2b3c4d, printed on the cover and on the notice page.",
          "The code contains no personal information, but it is linked to the reader record for which the copy was issued. This means that a copy distributed publicly may be traced back to the original download record.",
          "The code is used only to protect the individually issued nature of the book and to identify the source of unauthorized public distribution.",
        ],
      },
      {
        heading: "Retention",
        paras: [
          "Submitted information is retained until it is deleted upon request or removed during routine maintenance.",
          "Attribution information stored in the reader’s browser expires automatically after 90 days or 30 days, depending on the type of attribution data.",
        ],
      },
    ],
  },
  terms: {
    label: "Legal",
    title: "Terms of Use",
    entityLine: "Biology Trader Education LLC",
    showPostal: true,
    body: [
      "By using this website, you agree to use the content for personal, non-commercial purposes only.",
      "All written material is the intellectual property of Biology Trader Education LLC. The book THE CAGE may be shared and forwarded freely in its complete, unmodified form. No part of it may be excerpted, reproduced or republished separately without written permission.",
      "You may not use the site in any way that could damage, disable, overburden, or impair it, or interfere with any other party’s use.",
      "These terms may be updated periodically. Continued use of the site constitutes acceptance of the current version.",
    ],
  },
  risk: {
    label: "Legal",
    title: "Risk Disclosure",
    updated: "July 2026",
    sections: [
      {
        heading: "Educational purpose",
        paras: [
          "Biology Trader Education LLC is an educational publishing company focused on trading psychology, risk structure, and trader behavior. Content provided through this website, THE CAGE, emails, tools, videos, live sessions, and community is for general educational and informational purposes only.",
          "Nothing provided by Biology Trader Education LLC constitutes investment, trading, financial, legal, or tax advice, or a recommendation to buy, sell, or hold any security, futures contract, commodity interest, or other financial instrument.",
        ],
      },
      {
        heading: "No advisory relationship",
        paras: [
          "Biology Trader Education LLC does not manage customer funds, execute trades on behalf of users, or provide individualized investment advice. Using this website or its materials does not create a broker-client, investment-adviser, commodity-trading-adviser, fiduciary, or other professional relationship.",
        ],
      },
      {
        heading: "Trading risk",
        paras: [
          "Futures, day trading, and leveraged financial products involve substantial risk and are not suitable for everyone. You may lose all capital committed to trading and, depending on the instrument and account structure, more. Never trade with money you cannot afford to lose.",
          "You remain solely responsible for your trading decisions, risk limits, account management, and results.",
        ],
      },
      {
        heading: "Performance disclaimer",
        paras: [
          "Any performance figures, account statements, journal excerpts, trade examples, charts, screenshots, or case studies represent the experience of the author or the specifically identified source.",
          "They are provided for educational and illustrative purposes only and do not represent what any reader should expect to achieve. Past performance does not guarantee future results. Results vary according to capital, risk, experience, execution, costs, market conditions, and individual behavior.",
          "Hypothetical, simulated, or reconstructed examples have inherent limitations and may benefit from hindsight. They do not represent actual trading unless clearly identified as such.",
        ],
      },
      {
        heading: "Reader reviews",
        paras: [
          "Reader reviews reflect the individual opinions and experiences of their authors. They are not guarantees of trading performance and do not represent results that other readers should expect.",
          "Reviews are moderated for spam, abuse, and personal information.",
          "A “Verified download” badge confirms only that access to the book was verified. It does not verify the reviewer’s identity, trading performance, or every statement contained in the review.",
        ],
      },
      {
        heading: "Third-party services",
        paras: [
          "Third-party company names, platform names, logos, products, and trademarks remain the property of their respective owners. References are made for identification purposes only and do not imply affiliation, sponsorship, or endorsement unless expressly stated.",
        ],
      },
    ],
    footerNote: "© 2026 Biology Trader Education LLC. All rights reserved.",
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind];
  return (
    <div data-testid={`page-legal-${kind}`}>
      <section>
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-24 md:pt-40 pb-24 md:pb-40">
          <div className="label-mono text-muted">{c.label}</div>
          <h1 className="font-display mt-10 text-5xl md:text-7xl leading-[1.02] tracking-tight">
            {c.title}
          </h1>
          <div className="mt-16 space-y-8 text-[17px] md:text-lg text-muted leading-[1.9] border-t border-hairline pt-12">
            {c.body &&
              c.body.map((p, i) =>
                p && typeof p === "object" && Array.isArray(p.list) ? (
                  <ul key={i} className="list-disc pl-6 space-y-2">
                    {p.list.map((li, j) => (
                      <li key={j}>{li}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i}>{p}</p>
                )
              )}
            {c.sections &&
              c.sections.map((s, i) => (
                <div key={i} className={i === 0 ? "" : "pt-2"}>
                  <div className="label-mono text-ink mb-4">{s.heading}</div>
                  <div className="space-y-6">
                    {s.paras.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </div>
              ))}
            {c.footerNote && <p className="text-ink">{c.footerNote}</p>}
          </div>
          {c.entityLine && (
            <p className="mt-16 label-mono text-muted">{c.entityLine}</p>
          )}
          {c.showPostal && (
            <p className="mt-3 label-mono text-muted" data-testid={`legal-postal-${kind}`}>{POSTAL_ADDRESS}</p>
          )}
          <p className={`${c.entityLine ? "mt-3" : "mt-16"} label-mono text-muted`}>
            Last updated · {c.updated || new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>
    </div>
  );
}
